/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================
*/

import { useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

// @mui material components
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import OnnaDevices from "layouts/project/components/onnaDevices";
import Apartments from "layouts/project/components/apartments";

import Footer from "components/Footer";

import { getOnnaDevicesState } from "store/actions/onnaDevicesActions";
import MDAppBar from "components/MDAppBar";
import EmailConfig from "layouts/project/components/emailConfig/EmailConfig";
import Occupancy from "layouts/project/components/occupancy";
import CloudApis from "layouts/project/components/cloudApis";
import Amenities from "./components/amenitiesConfig/Amenities";
import Services from "./components/servicesConfig/Services";
import Certifications from "./components/Certifications";
import MetricsConfig from "./components/MetricsConfig";
import CustomAppMobile from "./components/customAppMobile";
import NotificationsProjectOptions from "./components/notifications/NotificationsProjectOptions";

function AlertDialog({ open, handleClose, handleAgree }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <MDBox
        sx={({ palette: { white, background } }) => ({
          backgroundColor: darkMode ? background.sidenav : white.main,
        })}
      >
        <DialogTitle id="alert-dialog-title">{t("confirmation")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{t("stepNotUndo")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose}>{t("cancel")}</MDButton>
          <MDButton onClick={handleAgree} autoFocus>
            {t("OK")}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}

function ProjectSettings() {
  return null;
}

function Project({
  database,
  onnaDevices,
  apartments,
  project,
  sendGetOnnaDevicesState,
  projectId,
  index1,
  index2,
  mainUserId,
  clients,
  menuConfig,
  isProjectManager,
}) {
  const { t } = useTranslation();

  const { features } = project || {};

  const navigate = useNavigate();

  useEffect(() => {
    sendGetOnnaDevicesState(Object.keys(onnaDevices));
  }, [JSON.stringify(onnaDevices)]);

  useEffect(() => {
    const html = document.documentElement;
    const previousOverflowY = html.style.overflowY;
    const previousScrollbarGutter = html.style.scrollbarGutter;

    html.style.overflowY = "scroll";
    html.style.scrollbarGutter = "stable";

    return () => {
      html.style.overflowY = previousOverflowY;
      html.style.scrollbarGutter = previousScrollbarGutter;
    };
  }, []);

  const tabConfig = [
    (isProjectManager
      ? features?.apartments && menuConfig?.apartments?.visible
      : menuConfig?.apartments?.visible) && {
      key: "apartments",
      icon: "house",
      component: Apartments,
      label: menuConfig?.apartments?.label,
      defaultLabel: menuConfig?.apartments?.defaultLabel,
    },
    features?.amenities &&
      menuConfig?.amenities?.visible && {
        key: "amenities",
        icon: "access_time",
        component: Amenities,
        label: menuConfig?.amenities?.label,
        defaultLabel: menuConfig?.amenities?.defaultLabel,
      },
    features?.services &&
      menuConfig?.services?.visible && {
        key: "services",
        icon: "room_service",
        component: Services,
        label: menuConfig?.services?.label,
        defaultLabel: menuConfig?.services?.defaultLabel,
      },
    (isProjectManager
      ? features?.settings && menuConfig?.settings?.visible
      : menuConfig?.settings?.visible) && {
      key: "settings",
      icon: "settings",
      component: ProjectSettings,
      label: menuConfig?.settings?.label,
      defaultLabel: menuConfig?.settings?.defaultLabel,
    },
    features?.certifications &&
      menuConfig?.certifications?.visible && {
        key: "certifications",
        icon: "verified",
        component: Certifications,
        label: menuConfig?.certifications?.label,
        defaultLabel: menuConfig?.certifications?.defaultLabel,
      },

    (isProjectManager
      ? features?.devices && menuConfig?.onnaDevices?.visible
      : menuConfig?.onnaDevices?.visible) && {
      key: "onnaDevices",
      icon: "hub",
      component: OnnaDevices,
      label: menuConfig?.onnaDevices?.label,
      defaultLabel: menuConfig?.onnaDevices?.defaultLabel,
    },
    (isProjectManager
      ? features?.occupancy && menuConfig?.occupancy?.visible
      : menuConfig?.occupancy?.visible) && {
      key: "occupancy",
      icon: "group",
      component: Occupancy,
      label: menuConfig?.occupancy?.label,
      defaultLabel: menuConfig?.occupancy?.defaultLabel,
    },
    (isProjectManager
      ? features?.cloudApis && menuConfig?.cloudApis?.visible
      : menuConfig?.cloudApis?.visible) && {
      key: "cloudApis",
      icon: "cloud",
      component: CloudApis,
      label: menuConfig?.cloudApis?.label,
      defaultLabel: menuConfig?.cloudApis?.defaultLabel,
    },
  ].filter(Boolean); // This removes the 'false' entries from the array

  const tabConfigSettings = [];

  if (
    isProjectManager
      ? features?.analytics && features?.metricsConfig && menuConfig?.analytics?.visible
      : features?.analytics && menuConfig?.analytics?.visible
  )
    tabConfigSettings.push({
      key: "metricsConfig",
      icon: "query_stats",
      component: MetricsConfig,
      label: menuConfig?.analytics?.label,
      defaultLabel: menuConfig?.analytics?.defaultLabel,
    });

  if (isProjectManager ? features?.email && menuConfig?.email?.visible : menuConfig?.email?.visible)
    tabConfigSettings.push({
      key: "email",
      icon: "mail",
      component: EmailConfig,
      label: menuConfig?.email?.label,
      defaultLabel: menuConfig?.email?.defaultLabel,
    });

  if (
    isProjectManager
      ? features?.customApp && menuConfig?.customApp?.visible
      : menuConfig?.customApp?.visible
  )
    tabConfigSettings.push({
      key: "customApp",
      icon: "phone_android",
      component: CustomAppMobile,
      label: menuConfig?.customApp?.label,
      defaultLabel: menuConfig?.customApp?.defaultLabel,
    });

  if (
    isProjectManager
      ? features?.notifications && menuConfig?.notifications?.visible
      : menuConfig?.notifications?.visible
  )
    tabConfigSettings.push({
      key: "notifications",
      icon: "notifications",
      component: NotificationsProjectOptions,
      label: menuConfig?.notifications?.label,
      defaultLabel: menuConfig?.notifications?.defaultLabel,
    });

  const isAnyTagVisible = tabConfig.length > 0;

  // Which primary keys spawn a secondary bar
  const secondaryTabsByKey = {
    settings: tabConfigSettings,
  };

  const primaryTabIndex = tabConfig.findIndex((tab) => tab.key === index1);
  const tabValue = primaryTabIndex >= 0 ? primaryTabIndex : 0;

  const tabsInfo = tabConfig.map((tab) => ({
    label: tab.label && String(tab.label).trim() !== "" ? tab.label : t(tab.defaultLabel),
    icon: tab.icon,
  }));
  const activePrimary = tabConfig[tabValue];
  const activeSecondaryConfig = secondaryTabsByKey[activePrimary?.key] || [];
  const hasSecondary = activeSecondaryConfig.length > 0;
  const secondaryTabIndex = activeSecondaryConfig.findIndex((tab) => tab.key === index2);
  const tab2Value = secondaryTabIndex >= 0 ? secondaryTabIndex : 0;

  const tabsInfoSettings = activeSecondaryConfig.map((tab) => ({
    label: tab.label && String(tab.label).trim() !== "" ? tab.label : t(tab.defaultLabel),
    icon: tab.icon,
  }));

  // --- Simple CSS (no Slide, no METRICS) --------------------------------
  // Sticky wrapper: placed right after DashboardNavbar. Primary is "normal" so it sets the height.
  // Secondary is absolutely positioned behind and uses transform for the motion.
  const navLayerSx = (theme) => ({
    position: "sticky",
    mt: 3,
    top: 0, // if your DashboardNavbar is also sticky, set a static offset instead (e.g., 64)
    zIndex: theme.zIndex.appBar, // base layer for both navbars
    [theme.breakpoints.down("md")]: {
      position: "static",
      top: "auto",
    },
  });

  const primarySx = (theme) => ({
    position: "relative", // participates in layout; defines the layer's height
    zIndex: theme.zIndex.appBar + 1,
  });

  const secondarySx = (theme) => ({
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    zIndex: theme.zIndex.appBar, // behind primary
    // transform: "translateY(0%)",
    transform: hasSecondary ? "translateY(120%)" : "translateY(0%)", //
    transition: "transform 800ms ease",
    willChange: "transform",
    pointerEvents: "auto",
    [theme.breakpoints.down("md")]: {
      position: "relative",
      left: "auto",
      right: "auto",
      top: "auto",
      zIndex: theme.zIndex.appBar + 1,
      transform: "none",
      transition: "none",
      willChange: "auto",
      mt: 1,
    },
  });
  // ---------------------------------------------------------------------------

  return (
    <DashboardLayout>
      <AlertDialog open={false} handleClose={() => {}} handleAgree={() => {}} />
      <Modal
        open={false}
        onClose={() => {}}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{ overflow: "scroll" }}
      >
        <MDBox
          mt={3}
          sx={12}
          sm={8}
          style={{
            position: "absolute",
            width: "65vw",
            left: "50%",
            transform: "translate(-50%, 0%)",
          }}
        >
          <div>FORM</div>
        </MDBox>
      </Modal>
      <DashboardNavbar
        title={project?.name}
        route={`/projects/${project?.name}`}
        firstLink="/projects/"
      />
      {/* Sticky layer for both navbars (simple + robust) */}
      {isAnyTagVisible && (
        <MDBox sx={navLayerSx}>
          {/* PRIMARY (front, normal flow → sets the height) */}
          <MDBox sx={primarySx}>
            <MDAppBar
              tabsInfo={tabsInfo}
              tabValue={tabValue}
              setTabValue={(indexValue) => {
                navigate(`/projects/${projectId}/${tabConfig[indexValue].key}`);
              }}
            />
          </MDBox>
          {/* SECONDARY (behind, absolutely positioned) */}
          <MDBox sx={secondarySx}>
            {hasSecondary && (
              <MDAppBar
                tabsInfo={tabsInfoSettings}
                tabValue={tab2Value}
                setTabValue={(indexValue) => {
                  navigate(
                    `/projects/${projectId}/${activePrimary.key}/${activeSecondaryConfig[indexValue].key}`
                  );
                }}
                isSecondary
              />
            )}
          </MDBox>
        </MDBox>
      )}
      {/* Content */}
      {hasSecondary
        ? activeSecondaryConfig.map((tab, idx) =>
            tab2Value === idx ? (
              <MDBox key={`secondary-${tab.key}`} pt={10} pb={3}>
                <tab.component
                  database={database}
                  projectId={projectId}
                  mainUserId={mainUserId}
                  apartments={apartments}
                  onnaDevices={onnaDevices}
                  clients={clients}
                  project={project}
                />
              </MDBox>
            ) : null
          )
        : null}

      {tabConfig.map((tab, idx) =>
        tabValue === idx ? (
          <MDBox key={`primary-${tab.key}`} pt={4} pb={3}>
            <tab.component
              database={database}
              projectId={projectId}
              mainUserId={mainUserId}
              apartments={apartments}
              onnaDevices={onnaDevices}
              clients={clients}
              project={project}
            />
          </MDBox>
        ) : null
      )}
      <Footer />
    </DashboardLayout>
  );
}

Project.defaultProps = {
  onnaDevices: {},
  apartments: {},
  project: {},
  projectId: "",
  index1: "",
  index2: "",
  mainUserId: "",
  clients: {},
  menuConfig: {},
};

Project.propTypes = {
  database: PropTypes.object.isRequired,
  onnaDevices: PropTypes.object,
  apartments: PropTypes.object,
  sendGetOnnaDevicesState: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  index1: PropTypes.string,
  index2: PropTypes.string,
  project: PropTypes.object,
  clients: PropTypes.object,
  mainUserId: PropTypes.string,
  menuConfig: PropTypes.object,
  isProjectManager: PropTypes.bool.isRequired,
};

AlertDialog.defaultProps = {
  handleAgree: () => {},
};

AlertDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAgree: PropTypes.func,
};
const mapStateToProps = (state, { projectId }) => {
  const {
    projectsReducer,
    onnaDevicesReducer,
    apartmentsReducer,
    clientsReducer,
    mainUserReducer,
  } = state;

  const project = projectsReducer ? projectsReducer[projectId] : {};
  const onnaDevicesIds = project?.onnaDevices;

  const apartmentsIds = project?.apartments;

  const onnaDevices = {};

  const apartments = {};

  if (onnaDevicesIds) {
    Object.keys(onnaDevicesIds).forEach((id) => {
      if (Object.prototype.hasOwnProperty.call(onnaDevicesReducer, id)) {
        onnaDevices[id] = onnaDevicesReducer[id];
      }
    });
  }

  if (apartmentsIds) {
    Object.keys(apartmentsIds).forEach((id) => {
      if (Object.prototype.hasOwnProperty.call(apartmentsReducer, id)) {
        apartments[id] = apartmentsReducer[id];
      }
    });
  }

  return {
    project,
    onnaDevices,
    apartments,
    clients: clientsReducer,
    menuConfig: mainUserReducer?.customApp?.menuConfig || {},
    mainUserId: mainUserReducer?.uid || null,
    isProjectManager: mainUserReducer?.isProjectManager || false,
  };
};

const mapDispatchToProps = (dispatch) => ({
  sendGetOnnaDevicesState: (onnaIds) => {
    dispatch(getOnnaDevicesState(onnaIds));
  },
});

const ProjectWithConnect = connect(mapStateToProps, mapDispatchToProps)(Project);

function ProjectWrapper(props) {
  const { projectId, index1, index2 } = useParams();
  return <ProjectWithConnect projectId={projectId} index1={index1} index2={index2} {...props} />;
}

export default ProjectWrapper;
